import { EnumUtility } from '@codequality/vue-cli-base';
import _ from 'lodash';

import {
  EOperator,
  Eフェーズ種別,
  E入力分類,
  E勘定科目カテゴリ,
  E有無フラグ,
  E科目別計画明細ConfigType,
  E科目別計画設定番号,
  E端数処理,
  E補助科目フラグ,
  E製造原価科目未使用フラグ,
  E集計転記科目,
} from '@/openapi/enum';
import {
  CデータPlbscfItem,
  Cデータ科目別計画ConfigItem,
  データListItemResponse,
  勘定科目ListItemResponse,
  科目別計画明細Config,
  科目別計画明細Formula,
  部門ListItemResponse,
} from '@/openapi/user';

import { MONTHS_OF_YEAR, 補助科目設定不可大分類s } from '@/classes/common/Constants';
import 決算期Utility, { 決算年, 決算期, 決算期TableHeader } from '@/classes/common/決算期Utility';
import AbstractPlbscfService, {
  CalcResultMap,
  KeyOldLoan,
  PartialCデータPlbscfItem,
  PlbscfItemValue,
  PlbscfListItem,
  PlbscfTypeList,
  PlbscfYearItem,
  PlbscfYearValue,
  TデータPlbscf,
  YearItemType,
  科目別計画明細_CONFIG_COUNT,
  貸借CalcConfig,
} from '@/classes/service/AbstractPlbscfService';
import AbstractデータService, { 帳票データ } from '@/classes/service/AbstractデータService';
import 分析PlbscfService from '@/classes/service/分析/分析PlbscfService';
import 償却Service from '@/classes/service/分析計画/償却Service';
import { 償却 } from '@/classes/service/分析計画/償却Type';
import 返済明細Service, { TableType, TデータOldLoan } from '@/classes/service/計画/返済明細Service';
import { PlbscfType, RowCssClass, ValueType } from '@/master/Plbscf';

// ------------------------------------------------------------------
// interface
// ------------------------------------------------------------------

interface 科目別ListItem {
  yearItems: PlbscfYearItem[];
  通常科目?: {
    name: string;
    itemCount: number;
  };
  monitoring?: {
    yearItem: PlbscfYearItem;
    業績予測: 業績予測;
  };
}

// ------------------------------------------------------------------
// enums
// ------------------------------------------------------------------

/**
 * PLBSCF種別
 */
export enum 帳票Type {
  標準,
  補助科目別,
  部門別,
  科目別,
}

// ------------------------------------------------------------------
// types
// ------------------------------------------------------------------

/**
 * 科目別計画のtデータ保持用
 */
export type Tデータ科目別計画Config = {
  データpk?: number;
  部門pk: number;
  items: Cデータ科目別計画ConfigItem[];
};
/**
 * 補助科目別計画の一覧要素
 */
export type 補助科目別計画ListItem = {
  通常科目?: {
    name: string;
    itemCount: number;
  };
  補助科目: string;
  canEdit: boolean;
  yearItems: PlbscfYearItem[]; // 年分
};
/**
 * 科目別計画の一覧要素
 */
export type 科目別計画ListItem = {
  勘定科目Pk: number;
  通常科目Pk: number; // 補助科目の場合は、勘定科目Pkと異なる
  通常科目?: {
    name: string;
    itemCount: number;
  };
  補助科目?: {
    name: string;
    itemCount: number;
  };
  config?: 科目別計画明細Config & { disMonths?: number[] }; // 通常科目（指定なし No.10）、合計はundefinedで編集不可
  計算項目No?: number; // 計算項目　として表示する番号、科目別計画明細No とは異なるラベル的な値
  // y補助科目, y通常科目, yConfig, y計算項目No for yearConfig
  y補助科目?: {
    name: string;
    itemCount: number;
  };
  y通常科目?: {
    name: string;
    itemCount: number;
  };
  yConfig?: 科目別計画明細Config & { disMonths?: number[] }; // 通常科目（指定なし No.10）、合計はundefinedで編集不可
  y計算項目No?: number;
  yearItems: PlbscfYearItem[]; // 年分
  option?: {
    label?: string;
    cssClass?: string;
  };

  // 以下、モニタリングで使用
  monitoring?: {
    yearItem: PlbscfYearItem;
    業績予測: 業績予測;
  };
};
export type 科目別計画 = {
  部門pk: number;
  list: 科目別計画ListItem[]; // size：通常科目数
};
/**
 * 業績予測
 */
export type 業績予測 = {
  // 前期/計画 のプルダウンによって再計算
  差異?: number;
  比率?: number;
};
/**
 * 貸借・損益の一行の情報
 */
export type Plbscf業績予測ListItem = PlbscfListItem & {
  業績予測?: 業績予測;
};
/**
 * 貸借・損益共通
 */
type Plbs = {
  header: 決算期TableHeader[]; // size：列数
  list: Plbscf業績予測ListItem[]; // size：行数
};
/**
 * 貸借
 */
export type Bs = Plbs & {
  貸借CalcConfig?: 貸借CalcConfig;
};
/**
 * 損益
 */
export type Pl = Plbs & {
  部門pk?: number; // 合計データの場合は設定しない
};

/**
 * parse の返却値
 */
export type 計画Plbscf = {
  bs: Bs;
  pls: Pl[]; // size：部門数 + 1（合計）
  types: PlbscfTypeList[]; // size：貸借・損益以外のPlbscfType数
  補助科目別貸借対照表: {
    header: 決算期TableHeader[]; // size：列数
    list: 補助科目別計画ListItem[]; // size：補助科目を持つことができる通常科目数
  };
  科目別損益計算書: {
    header: 決算期TableHeader[]; // size：列数
    科目別計画s: 科目別計画[]; // size：部門数
  };
};

export default abstract class Abstract計画PlbscfService<T> extends AbstractPlbscfService<T> {
  public static readonly defaultVisible科目別計画明細Nos = [1, 2, 科目別計画明細_CONFIG_COUNT];

  protected static 計画Plbscf: 計画Plbscf | null = null;

  protected 分析PlbscfService: 分析PlbscfService | null = null;

  protected 償却Service: 償却Service | null = null;

  protected 返済明細Service: 返済明細Service | null = null;

  protected 分析PlbscfTypeLists: PlbscfTypeList[] = [];

  protected 償却: 償却 | null = null;

  protected oldLoans: TデータOldLoan | null = null;

  /** 科目別計画のtデータ */
  protected tデータ科目別計画Configs: Tデータ科目別計画Config[] = [];

  /** YearConfigのtデータ */
  protected tデータYearConfigs: Tデータ科目別計画Config[] = [];

  /** 計算用科目別計画データ */
  protected calc科目別計画s: 科目別計画[] = [];

  /** 月次も含む全ての決算期 */
  protected all決算期: 決算期 = {
    header: [],
    years: [],
  };

  /** 年次のみの決算期 */
  protected onlyYear決算期: 決算期 = {
    header: [],
    years: [],
  };

  protected is帳票 = false;

  /**
   * 端数処理
   *
   * @params n
   * @returns
   */
  private roundFunc: (n: number) => number = Math.ceil;

  // ------------------------------------------------------------------
  // getter/setter
  // ------------------------------------------------------------------

  /**
   * getter
   */
  public get getTデータ科目別計画Configs(): Tデータ科目別計画Config[] {
    return this.tデータ科目別計画Configs;
  }
  public get getTデータYearConfigs(): Tデータ科目別計画Config[] {
    return this.tデータYearConfigs;
  }
  public get getPropertiesToCopyMonitoring(): {
    分析PlbscfService: 分析PlbscfService | null;
    償却Service: 償却Service | null;
    返済明細Service: 返済明細Service | null;
    分析PlbscfTypeLists: PlbscfTypeList[];
    償却: 償却 | null;
  } {
    return {
      分析PlbscfService: this.分析PlbscfService,
      償却Service: this.償却Service,
      返済明細Service: this.返済明細Service,
      分析PlbscfTypeLists: this.分析PlbscfTypeLists,
      償却: this.償却,
    };
  }
  public get getCalcTypeLists(): PlbscfTypeList[] {
    return this.calcTypeLists;
  }
  public get getCalc科目別計画s(): 科目別計画[] {
    return this.calc科目別計画s;
  }
  /**
   * setter
   */
  public set setIs帳票(is帳票: boolean) {
    this.is帳票 = is帳票;
  }

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  /**
   * @override
   * @param eフェーズ種別
   */
  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);

    switch (AbstractデータService.事業所.c実績値及び計画値端数処理) {
      case E端数処理.切上げ:
        this.roundFunc = Math.ceil;
        break;
      case E端数処理.切捨て:
        this.roundFunc = Math.floor;
        break;
      case E端数処理.四捨五入:
        this.roundFunc = Math.round;
        break;
    }
  }

  // ------------------------------------------------------------------
  // public static methods
  // ------------------------------------------------------------------

  /**
   * 計算式を作成します
   *
   * @param values
   * @param operators operators.length = values.length - 1
   * @param isScript
   * @returns
   */
  public static createFormula(values: number[], operators: EOperator[], isScript: boolean): string {
    if (values.length !== operators.length + 1) throw new Error('values vs operators length mismatch');
    if (values.length === 1) return String(values[0]);

    return operators
      .map((o, i) => {
        let scriptOperator = o as string;
        if (isScript) {
          switch (o) {
            case EOperator.multiplication:
              scriptOperator = '*';
              break;
            case EOperator.division:
              scriptOperator = '/';
              break;
            default:
              scriptOperator = o;
              break;
          }
        }
        return (i === 0 ? values[i] : '') + scriptOperator + values[i + 1];
      })
      .join('');
  }

  /**
   * 科目別計画明細種別ラベル
   *
   * @param config
   * @returns
   */
  public static getConfigTypeLabel(config: 科目別計画明細Config): string {
    if (config.type !== E科目別計画明細ConfigType.計算式)
      return EnumUtility.valueToKey(E科目別計画明細ConfigType, config.type);

    const formula = config.formula;
    if (formula === undefined) throw new Error('no formula');
    return this.createFormula(formula.nos, formula.operators, false);
  }

  /**
   * 初期化
   *
   * ※帳票一括印刷 のボタン押下時、選択した帳票でループする前に本処理を実行する（別タブでデータが更新されている可能性があるため）
   * ※帳票一括印刷 の最中（ループ中）は、再計算させないために「this.計画Plbscf」を使用して帳票出力する
   */
  public static initialize(): void {
    this.計画Plbscf = null;
  }

  /**
   * 科目別計画明細Config から一覧/帳票での表示可否を判定します
   *
   * @param config
   * @returns
   */
  public static isVisible科目別計画明細Config(config?: 科目別計画明細Config): boolean {
    return (
      config?.科目別計画明細No === undefined ||
      this.defaultVisible科目別計画明細Nos.includes(config.科目別計画明細No) ||
      config.isVisible === true
    );
  }

  /**
   * 科目別計画明細Config から一覧/帳票での表示可否を判定します
   *
   * @param listItem
   * @returns
   */
  public static isVisible科目別計画ListItem(listItem: 科目別計画ListItem, isYear = false): boolean {
    return this.isVisible科目別計画明細Config(isYear ? listItem.yConfig : listItem.config);
  }

  /**
   * 月次の値を取得する
   *
   * @param yv
   * @returns
   */
  public static getMonthlyValues(yv: PlbscfYearValue): number[] {
    if (yv.item.monthlyValues !== undefined) return yv.item.monthlyValues;
    if (yv.monthlyCalcValues !== undefined) return yv.monthlyCalcValues;
    return new Array(MONTHS_OF_YEAR).fill(0);
  }

  // ------------------------------------------------------------------
  // override protected methods
  // ------------------------------------------------------------------

  // @override
  protected getValueByCode(
    calcResultMap: CalcResultMap,
    itemValues: PlbscfItemValue[],
    code: string,
    year: number,
    monthlyIndex?: number,
    部門pk?: number,
    isYear = false
  ): number {
    // year：過去決算期の場合、分析の計算結果を取得する
    if (決算期Utility.diffYearFrom0期(year) < 0) {
      return this.分析PlbscfService!.getValueBy分析CalcResultMap(itemValues, code, year);
    }
    return super.getValueByCode(calcResultMap, itemValues, code, year, monthlyIndex, 部門pk, isYear);
  }

  // ------------------------------------------------------------------
  // protected methods
  // ------------------------------------------------------------------

  /**
   * 科目別損益計算書の一覧部を作成
   *
   * @param 部門pk
   * @param 計画List
   * @returns
   */
  protected create科目別計画ListItems(部門pk: number, 計画List: 科目別計画ListItem[] = []): 科目別計画ListItem[] {
    const okPks = this.勘定科目s
      .filter((k) => k.c科目別計画フラグ === E有無フラグ.あり && this.canSet補助科目(k))
      .map((k) => k.pk);
    let 部門ConfigItem = this.tデータ科目別計画Configs.find((d) => d.部門pk === 部門pk);
    if (部門ConfigItem === undefined) {
      部門ConfigItem = {
        部門pk,
        items: [],
      };
      this.tデータ科目別計画Configs.push(部門ConfigItem);
    }

    if (this.tデータYearConfigs.length === 0) {
      this.tデータYearConfigs = JSON.parse(JSON.stringify(this.tデータ科目別計画Configs));
      this.tデータYearConfigs.forEach((val) => {
        val.データpk = undefined;
      });
    }
    let 部門ConfigYearItem = this.tデータYearConfigs.find((d) => d.部門pk === 部門pk);
    if (部門ConfigYearItem === undefined) {
      部門ConfigYearItem = {
        部門pk,
        items: [],
      };
      this.tデータYearConfigs.push(部門ConfigYearItem);
    }

    const configItems = 部門ConfigItem.items;
    const configYearItems = 部門ConfigYearItem.items;
    const newConfig = (科目別計画明細No: number): 科目別計画明細Config => ({
      科目別計画明細No,
      type: E科目別計画明細ConfigType.直接入力,
      label: 科目別計画明細No === 科目別計画明細_CONFIG_COUNT ? '小計' : '',
    });
    return this.分析PlbscfTypeLists
      .find((t) => t.type === PlbscfType.損益計算書)!
      .list.filter((plbscfListItem) => {
        const pk = plbscfListItem.yearValues[0].item.勘定科目Pk;
        if (pk === undefined) return false;
        return okPks.includes(pk);
      })
      .flatMap((plbscfListItem) => {
        const 通常科目Pk = plbscfListItem.勘定科目!.pk;
        const 通常科目名 = plbscfListItem.config.label.name;

        const 補助科目s = this.勘定科目s
          .filter((hk) => hk.c勘定科目 === 通常科目名 && hk.c補助科目フラグ === E補助科目フラグ.補助)
          .map((hk) => ({
            pk: hk.pk,
            name: hk.c補助科目!,
          }));
        補助科目s.push({
          pk: plbscfListItem.yearValues[0].item.勘定科目Pk!,
          name: '指定なし',
        });

        const has補助科目s = 補助科目s.length > 1;
        const 通常科目 = {
          name: 通常科目名,
          itemCount: has補助科目s ? 1 : 0,
        };

        const y通常科目 = {
          name: 通常科目名,
          itemCount: has補助科目s ? 1 : 0,
        };

        const 科目別計画ListItems: 科目別計画ListItem[] = 補助科目s.flatMap((hk, i) => {
          let configItem: Cデータ科目別計画ConfigItem | undefined = configItems.find((c) => c.勘定科目Pk === hk.pk);
          let configYearItem: Cデータ科目別計画ConfigItem | undefined = configYearItems.find(
            (c) => c.勘定科目Pk === hk.pk
          );
          if (configItem === undefined || configYearItem === undefined) {
            const configs: 科目別計画明細Config[] = [];
            for (let no = 1; no <= 科目別計画明細_CONFIG_COUNT; no++) configs.push(newConfig(no));
            if (configItem === undefined) {
              configItem = {
                勘定科目Pk: hk.pk,
                configs,
              };
              configItems.push(configItem); // DB保存用
            }
            if (configYearItem === undefined) {
              configYearItem = {
                勘定科目Pk: hk.pk,
                configs,
              };
              configYearItems.push(configYearItem); // DB保存用
            }
          }

          const 補助科目 = {
            name: hk.name,
            itemCount: configItem.configs.filter((c) => Abstract計画PlbscfService.isVisible科目別計画明細Config(c))
              .length,
          };
          通常科目.itemCount += 補助科目.itemCount;

          const y補助科目 = {
            name: hk.name,
            itemCount: configYearItem.configs.filter((c) => Abstract計画PlbscfService.isVisible科目別計画明細Config(c))
              .length,
          };
          y通常科目.itemCount += y補助科目.itemCount;

          const configListItems: 科目別計画ListItem[] = configItem.configs.map((config, j) => {
            const 勘定科目Pk = hk.pk;
            const is小計 = j === 科目別計画明細_CONFIG_COUNT - 1;
            const can取込 = i === 補助科目s.length - 1 && is小計;
            let 計算項目No = undefined;
            if (config.科目別計画明細No < 補助科目.itemCount) 計算項目No = config.科目別計画明細No;
            else if (config.科目別計画明細No === 科目別計画明細_CONFIG_COUNT) 計算項目No = 補助科目.itemCount;

            let y計算項目No = undefined;
            const yConfig = configYearItem?.configs[j];
            if (yConfig != undefined) {
              if (yConfig.科目別計画明細No < y補助科目.itemCount) y計算項目No = yConfig.科目別計画明細No;
              else if (yConfig.科目別計画明細No === 科目別計画明細_CONFIG_COUNT) {
                y計算項目No = y補助科目.itemCount;
              }
            }

            return {
              勘定科目Pk,
              通常科目Pk,
              補助科目: j === 0 ? 補助科目 : undefined,
              config,
              計算項目No,
              y計算項目No,
              y補助科目: j === 0 ? y補助科目 : undefined,
              yConfig,
              yearItems: can取込
                ? this.create科目別YearItems(plbscfListItem, this.all決算期, 部門pk)
                : this.getYearItems(this.all決算期, 勘定科目Pk, false, 部門pk, config),
              option: is小計
                ? {
                    cssClass: 'blue',
                  }
                : undefined,
              monitoring: this.createMonitoringFor科目別計画(計画List, 勘定科目Pk, 通常科目Pk, config.科目別計画明細No),
            };
          });

          // 必要なら計算
          const calcListItems = configListItems.filter((li) => li.config?.type === E科目別計画明細ConfigType.計算式);
          if (calcListItems.length > 0) {
            calcListItems
              // 他の項目から参照されていない項目から計算
              .sort((first, second) => {
                const config1 = first.config!;
                const config2 = second.config!;
                if (config2.formula!.nos.includes(config1.科目別計画明細No)) return -1;
                if (config1.formula!.nos.includes(config2.科目別計画明細No)) return 1;
                return 0;
              })
              .forEach((li) => {
                const formula = li.config!.formula!;
                li.yearItems.forEach((yi, i) => {
                  if (yi.type === undefined)
                    yi.item.value = this.calculateByConfig(formula, configListItems, i, 'value');
                  const monthlyValues = yi.item.monthlyValues;
                  if (monthlyValues === undefined) return;
                  monthlyValues.forEach((_, j) => {
                    if (li.config!.disMonths! != undefined && li.config!.disMonths!.includes(j)) return;
                    monthlyValues[j] = this.calculateByConfig(formula, configListItems, i, 'monthlyValues', j);
                  });
                });
              });
          }

          const yCalcListItems = configListItems.filter((li) => li.yConfig?.type === E科目別計画明細ConfigType.計算式);
          if (yCalcListItems.length > 0) {
            yCalcListItems
              // 他の項目から参照されていない項目から計算
              .sort((first, second) => {
                const config1 = first.yConfig!;
                const config2 = second.yConfig!;
                if (config2.formula!.nos.includes(config1.科目別計画明細No)) return -1;
                if (config1.formula!.nos.includes(config2.科目別計画明細No)) return 1;
                return 0;
              })
              .forEach((li) => {
                const formula = li.yConfig!.formula!;
                li.yearItems.forEach((yi, i) => {
                  if (yi.type === undefined)
                    yi.item.yValue = this.calculateByConfig(formula, configListItems, i, 'yValue');
                });
              });
          }
          return configListItems;
        });

        // 通常科目全体の行数を設定
        科目別計画ListItems[0].通常科目 = 通常科目;
        科目別計画ListItems[0].y通常科目 = y通常科目;

        // 合計
        if (has補助科目s) {
          const 勘定科目Pk = plbscfListItem.yearValues[0].item.勘定科目Pk!;
          科目別計画ListItems.push({
            勘定科目Pk,
            通常科目Pk,
            補助科目: {
              name: '',
              itemCount: 1,
            },
            y補助科目: {
              name: '',
              itemCount: 1,
            },
            yearItems: this.sumPlbscfYearItems(
              科目別計画ListItems.filter((item) => item.config?.科目別計画明細No === 科目別計画明細_CONFIG_COUNT)
            ),
            option: {
              label: '合計',
            },
            monitoring: this.createMonitoringFor科目別計画(計画List, 勘定科目Pk, 通常科目Pk),
          });
        }
        return 科目別計画ListItems;
      });
  }

  /**
   * 計算用データを作成、設定
   *
   * @param 計画TypeLists
   * @returns
   */
  protected setCalcTypeLists(計画TypeLists: PlbscfTypeList[] = []): void {
    // 画面表示用データの追加処理
    const addPlbscfTypeList = (
      v分析PlbscfTypeList: PlbscfTypeList,
      v決算期: 決算期,
      calcYearValues: PlbscfYearValue[],
      部門?: 部門ListItemResponse
    ): void => {
      const list: PlbscfListItem[] = [];
      const plbscfType = v分析PlbscfTypeList.type;
      const is貸借 = plbscfType === PlbscfType.貸借対照表;
      const is損益 = plbscfType === PlbscfType.損益計算書;
      const 計画TypeList = 計画TypeLists.find((t) => t.type === plbscfType && t.部門pk === 部門?.pk);
      // 行でループ
      v分析PlbscfTypeList.list.forEach((v分析ListItem, i) => {
        // 一行設定
        list.push({
          ...v分析ListItem,
          yearValues: v決算期.years.map((v決算年) =>
            this.createYearValue(v分析ListItem, v決算年, calcYearValues, 部門?.pk, is貸借, is損益)
          ),
          monitoring: this.createMonitoringForPlbscf(計画TypeList, i),
        });
      });
      this.calcTypeLists.push({
        type: plbscfType,
        header: v決算期.header,
        list,
        部門pk: 部門?.pk,
        貸借CalcConfig: is貸借 ? this.create貸借CalcConfig() : undefined,
      });
    };

    // 計算用データ初期化
    this.calcTypeLists = [];

    // 5期前を除いた年次のみの決算期（CF計算書用）
    const onlyYear決算期Without5期前: 決算期 = {
      header: this.onlyYear決算期.header.filter((h) => 決算期Utility.diffYearFrom0期(h.year) > -5),
      years: this.onlyYear決算期.years.filter((y) => 決算期Utility.diffYearFrom0期(y.year) > -5),
    };

    let calcYearValues: PlbscfYearValue[] = [];

    // ※分析に表示されている勘定科目の情報を計画でも表示したいので、
    // 　分析PlbscfService.parse() で取得した 分析PlbscfTypeLists を使用する
    for (const 分析PlbscfTypeList of this.分析PlbscfTypeLists) {
      const type = 分析PlbscfTypeList.type;
      // 貸借・損益：月次含む
      // CF計算書：年次のみ、5期前なし
      // 上記以外：年次のみ
      const 決算期 = [PlbscfType.貸借対照表, PlbscfType.損益計算書].includes(type)
        ? this.all決算期
        : type === PlbscfType.CF計算書
        ? onlyYear決算期Without5期前
        : this.onlyYear決算期;

      if (type === PlbscfType.損益計算書) {
        // 部門別
        for (const 部門 of this.部門s) {
          addPlbscfTypeList(分析PlbscfTypeList, 決算期, calcYearValues, 部門);
        }
        // 合計
        this.calcTypeLists.push({
          type,
          header: 決算期.header,
          list: [],
        });
        // 損益が終わったタイミングで、損益合計算出／値の転記
        // ※損益以降のtypeで、損益合計データを使えるようにするため、ここで諸々準備しておく
        this.transcribe();
        this.setTotal損益AndTranscribe(計画TypeLists);
        calcYearValues = this.filterYearValues(this.calcTypeLists);
      } else {
        // 通常のデータ
        addPlbscfTypeList(分析PlbscfTypeList, 決算期, calcYearValues);
      }
    }
  }

  /**
   * 引き数に紐付くPlbscfYearItem配列を取得
   * DBデータが存在すれば取得、存在しない場合は生成・DBデータに設定したうえで返却
   * 但し、科目別計画明細Config.typeが計算式で、DBデータが存在する場合は削除する
   *
   * @param v決算期
   * @param 勘定科目Pk
   * @param is貸借
   * @param 部門pk
   * @param config
   * @returns
   */
  protected getYearItems(
    v決算期: 決算期,
    勘定科目Pk: number,
    is貸借: boolean,
    部門pk?: number,
    config?: 科目別計画明細Config
  ): PlbscfYearItem[] {
    const is計算式 = config?.type === E科目別計画明細ConfigType.計算式;

    return v決算期.years.map((v決算年) => {
      const is分析 = v決算年.eフェーズ種別 === Eフェーズ種別.分析フェーズ;
      let item: CデータPlbscfItem;
      if (is分析 || is計算式) {
        item = this.createEmpty勘定科目Item(v決算年, 勘定科目Pk, config?.科目別計画明細No);

        // 分析以外の計算式はDBデータから CデータPlbscfItem を削除
        if (!is分析) this.removeCデータPlbscfItems(v決算年, 勘定科目Pk, 部門pk, config);
      } else {
        item = this.findOrCreate勘定科目Item(
          this.findCデータPlbscfItems(v決算年, 部門pk),
          v決算年,
          勘定科目Pk,
          config?.科目別計画明細No
        );
      }
      // 貸借 かつ 月次データが存在する場合、最終月の値を表示させたいので設定
      let optionValue: number | undefined = undefined;
      if (is貸借 && this.isMonthlyBy決算年(v決算年) && item.monthlyValues !== undefined) {
        optionValue = item.monthlyValues[MONTHS_OF_YEAR - 1];
      }

      if (item.yValue === undefined && item.value !== undefined) {
        item.yValue = item.value;
      }

      return {
        year: v決算年.year,
        item,
        type: is分析 ? YearItemType.readonly : undefined,
        optionValue,
      };
    });
  }

  /**
   * 引き数一覧要素内の CデータPlbscfItem.value/monnthlyValues を要素間で合計した PlbscfYearItem[] を返却
   *
   * @param list
   * @returns
   */
  protected sumPlbscfYearItems(list: Array<{ yearItems: PlbscfYearItem[] }>): PlbscfYearItem[] {
    const totalItems: PlbscfYearItem[] = _.cloneDeep(list[0].yearItems);
    for (let i = 1; i < list.length; i++) {
      const listItem = list[i];
      totalItems.forEach((yearItem, j) => {
        yearItem.item.value! += listItem.yearItems[j].item.value!;
        yearItem.item.yValue! += listItem.yearItems[j].item.yValue!;
        const mvs = listItem.yearItems[j].item.monthlyValues;
        if (mvs === undefined) return;
        mvs.forEach((mv, k) => (yearItem.item.monthlyValues![k] += mv));
        if (yearItem.optionValue !== undefined) yearItem.optionValue = yearItem.item.monthlyValues![MONTHS_OF_YEAR - 1];
      });
    }
    return totalItems;
  }

  /**
   * 損益の部門合計を算出し、値を転記
   *
   * @param 計画TypeLists
   * @returns
   */
  protected setTotal損益AndTranscribe(計画TypeLists: PlbscfTypeList[] = []): void {
    // 損益の部門合計を設定
    const 損益typeLists = this.calcTypeLists.filter((t) => t.type === PlbscfType.損益計算書 && t.部門pk !== undefined);
    const totalTypeList = this.calcTypeLists.find((t) => t.type === PlbscfType.損益計算書 && t.部門pk === undefined)!;
    const totalList = _.cloneDeep(損益typeLists[0].list);
    const 計画TotalTypeList = 計画TypeLists.find((t) => t.type === PlbscfType.損益計算書 && t.部門pk === undefined);
    for (let i = 1; i < 損益typeLists.length; i++) {
      const 損益typeList = 損益typeLists[i];
      totalList.forEach((listItem, j) => {
        const 損益ListItem = 損益typeList.list[j];
        listItem.yearValues.forEach((yv, k) => {
          // year：過去決算期の場合、何もしない
          if (決算期Utility.diffYearFrom0期(yv.year) < 0) return;
          const 損益yv = 損益ListItem.yearValues[k];
          if (損益yv.item.value !== undefined) {
            yv.item.value! += 損益yv.item.value;
          }
          if (損益yv.item.monthlyValues !== undefined) {
            損益yv.item.monthlyValues.forEach((損益mv, l) => (yv.item.monthlyValues![l] += 損益mv));
          }
        });
        listItem.monitoring = this.createMonitoringForPlbscf(計画TotalTypeList, j);
      });
    }
    totalTypeList.list = totalList;
  }

  /**
   * 科目別の値を損益に転記
   *
   * @returns
   */
  protected transcribe科目別ToPls(): void {
    // 科目別の値を転記
    const 損益typeLists = this.calcTypeLists.filter((t) => t.type === PlbscfType.損益計算書 && t.部門pk !== undefined);
    const copyPks = this.勘定科目s
      .filter((k) => k.c科目別計画フラグ === E有無フラグ.あり && this.canSet補助科目(k))
      .map((k) => k.pk);
    損益typeLists.forEach((typeList) => {
      const v科目別計画ListItems = this.calc科目別計画s
        .find((k) => k.部門pk === typeList.部門pk)!
        .list.filter((listItem) => {
          const pk = listItem.勘定科目Pk;
          if (pk === undefined) return false;
          return copyPks.includes(pk);
        });
      typeList.list.forEach((listItem) => {
        const v勘定科目Pk = listItem.yearValues[0].item.勘定科目Pk;
        if (v勘定科目Pk === undefined || !copyPks.includes(v勘定科目Pk)) return;
        const filteredListItems = v科目別計画ListItems.filter((listItem) => listItem.勘定科目Pk === v勘定科目Pk);
        let foundListItem = filteredListItems.find((listItem) => listItem.option?.label === '合計');
        if (foundListItem === undefined) {
          // 合計なし ならば、config の 指定なし No.10 を設定
          foundListItem = filteredListItems.find((listItem) => listItem.config?.科目別計画明細No === 10)!;
        }
        listItem.yearValues = this.all決算期.years.map((v決算年, i) => ({
          year: v決算年.year,
          type: YearItemType.readonly,
          item: {
            勘定科目Pk: v勘定科目Pk,
            value: foundListItem!.yearItems[i].item.value,
            yValue: foundListItem!.yearItems[i].item.yValue,
            monthlyValues: foundListItem!.yearItems[i].item.monthlyValues,
          },
        }));
        listItem.config = {
          ...listItem.config,
          rowCssClass: RowCssClass.greyRow,
        };
      });
    });
  }

  /**
   * 損益合計：「税引後当期純利益」を、貸借：「当期純利益」に転記
   *
   * @returns
   */
  protected transcribe当期純利益ToBs(): void {
    const 貸借TypeList = this.calcTypeLists.find((t) => t.type === PlbscfType.貸借対照表)!;

    // ※損益合計のデータだけ先に計算して「税引後当期純利益」を取得する
    const total損益CalcResultMap = this.getTotal損益CalcResultMap();
    const set当期純利益 = (yv: PlbscfYearValue): void => {
      const code = 'N225';
      const isYear = 決算期Utility.checkE計画表示モード() && 決算期Utility.diffYearFrom0期(yv.year, true) > 0;
      const itemValue = this.getValueByCode(total損益CalcResultMap, [], code, yv.year, undefined, undefined, isYear);
      const mvs = yv.item.monthlyValues;
      if (mvs === undefined) {
        yv.item.value = itemValue;
        return;
      }
      // 月のデータが存在する場合
      mvs.forEach((_, i) => {
        if (!this.isEditableMonth(i)) return;
        mvs[i] = this.getValueByCode(total損益CalcResultMap, [], code, yv.year, i);
        // 期首月（先頭の月）の翌月以降は、前月の値を足す
        if (i > 0) mvs[i] += mvs[i - 1];
      });
      yv.item.value = _.sum(yv.item.monthlyValues);
      yv.optionValue = isYear ? itemValue : mvs[MONTHS_OF_YEAR - 1];
    };
    this.transcribeToList(set当期純利益, 貸借TypeList, [E集計転記科目.当期純利益]);
  }

  /**
   * 貸借：「当期純利益」を、貸借：「繰越利益剰余金」に転記
   *
   * @returns
   */
  protected transcribe繰越利益剰余金ToBs(): void {
    const 貸借TypeList = this.calcTypeLists.find((t) => t.type === PlbscfType.貸借対照表)!;

    // 指定年のデータがDBにまだ登録されていない場合、貸借の「繰越利益剰余金」の値を設定
    const 繰越利益剰余金Item = 貸借TypeList.list.find((listItem) => listItem.勘定科目?.c勘定科目 === '繰越利益剰余金');
    const 当期純利益Item = 貸借TypeList.list.find(
      (listItem) => listItem.勘定科目?.c集計転記科目 === E集計転記科目.当期純利益
    );
    if (繰越利益剰余金Item !== undefined) {
      繰越利益剰余金Item.yearValues.forEach((yv, index) => {
        if (決算期Utility.diffYearFrom0期(yv.year) < 0) return; // || this.hasデータSaved(yv.year)
        // 前年期の繰越利益剰余金＋前年期の当期純利益
        let value = AbstractPlbscfService.getYearValue(繰越利益剰余金Item.yearValues[index - 1]);
        if (当期純利益Item !== undefined)
          value += AbstractPlbscfService.getYearValue(当期純利益Item.yearValues[index - 1]);

        const mvs = yv.item.monthlyValues;
        if (mvs === undefined) {
          yv.item.value = value;
        } else {
          // 月のデータが存在する場合、すべての月に上記の値を設定する
          mvs.forEach((_, i) => (mvs[i] = value));
          yv.item.value = _.sum(mvs);
          yv.optionValue = value;
        }
      });
    }
  }

  /**
   * 値の転記（損益合計以外）
   *
   * @returns
   */
  protected transcribe(): void {
    this.transcribe借入金ToBs();
  }

  /**
   * 償却：期末簿価を、貸借：「有形固定資産（土地を除く）」「無形固定資産（借地権を除く）」に転記
   *
   * @returns
   */
  protected transcribe期末簿価ToBs(): void {
    const 貸借TypeList = this.calcTypeLists.find((t) => t.type === PlbscfType.貸借対照表)!;

    // TODO: 暫定対応
    const 償却 = this.償却!;
    const 償却予測額 = 償却.total.償却予測額!;
    const set期末簿価 = (yv: PlbscfYearValue): void => {
      const found期末簿価 = 償却予測額.科目別期末簿価s.find((k) => k.勘定科目Pk === yv.item.勘定科目Pk);
      const foundValue = found期末簿価?.values.find((v, i) => 償却.header[i].year === yv.year);
      const value = foundValue === undefined ? 0 : foundValue;
      yv.item.value = value;
      const mvs = yv.item.monthlyValues;
      if (mvs === undefined) return;
      // 月のデータが存在する場合、最終月のみvalueを設定し、それ以外の月は0
      mvs.forEach((_, i) => (mvs[i] = i === MONTHS_OF_YEAR - 1 ? value : 0));
      yv.optionValue = value;
    };
    this.transcribeToList(set期末簿価, 貸借TypeList, [
      E集計転記科目.建物,
      E集計転記科目.建物附属設備,
      E集計転記科目.建物付属設備,
      E集計転記科目.設備造作,
      E集計転記科目.構築物,
      E集計転記科目.機械装置,
      E集計転記科目.車両運搬具,
      E集計転記科目.工具器具備品,
      E集計転記科目.什器備品,
      E集計転記科目.リース資産,
      E集計転記科目.一括償却資産,
      E集計転記科目.減価償却累計額,
      E集計転記科目.電話加入権,
      E集計転記科目.ソフトウェア,
      E集計転記科目.投資有価証券,
      E集計転記科目.関係会社株式,
      E集計転記科目.敷金,
      E集計転記科目.差入保証金,
      E集計転記科目.保証金,
      E集計転記科目.保険積立金,
      E集計転記科目.配当金積立金,
      E集計転記科目.長期前払費用,
      E集計転記科目.長期貸付金,
      E集計転記科目.預託金,
      E集計転記科目.出資金,
      E集計転記科目.リサイクル預託金,
      E集計転記科目.会員権,
      E集計転記科目.貸倒引当金投,
      E集計転記科目.創立費,
      E集計転記科目.開業費,
      E集計転記科目.開発費,
    ]);
  }

  /**
   * 返済明細：残高合計を、貸借：「短期/長期/役員借入金」に転記
   *
   * @returns
   */
  protected transcribe借入金ToBs(): void {
    const disableMonths = this.calcTypeLists[0].header?.filter((h) => h.month).map((h) => h.disabled!);
    const 貸借TypeList = this.calcTypeLists.find((t) => t.type === PlbscfType.貸借対照表)!;

    const set短期借入金 = (yv: PlbscfYearValue): void => {
      set借入金(yv, TableType.短期);
    };
    const set長期借入金 = (yv: PlbscfYearValue): void => {
      set借入金(yv, TableType.長期);
    };
    const set役員借入金 = (yv: PlbscfYearValue): void => {
      set借入金(yv, TableType.役員);
    };
    const set借入金 = (yv: PlbscfYearValue, tableType: TableType): void => {
      const 返済明細yr = this.返済明細Service!.get残高合計PlbscfYearResults(tableType).find(
        (yr) => yr.year === yv.year
      );
      const mvs = yv.item.monthlyValues;
      const lastIndex = MONTHS_OF_YEAR - 1;
      if (返済明細yr?.monthlyCalcValues === undefined) {
        // 2期以降（返済明細に月次の値がない）
        const yearlyValue = 返済明細yr?.calcValue;
        if (mvs === undefined) {
          // ・計画：年 → 年 への転記して終了
          this.setYearValue(yv, yearlyValue);
          return;
        } else {
          // ・業績予測：年 → 最終月 への転記
          if (this.isEditableMonth(lastIndex)) mvs[lastIndex] = yearlyValue!;
        }
      } else {
        // 0,1期：月 → 月 への転記
        if (mvs === undefined) return;
        mvs.forEach((_, i) => {
          if (this.isEditableMonth(i)) mvs[i] = 返済明細yr.monthlyCalcValues![i];
        });
      }
      yv.item.value = _.sum(mvs);
      yv.optionValue = mvs[lastIndex];

      // Setting for monthly values if has old data
      const typeLoan = tableType.replace('借入金返済予定表', '') as KeyOldLoan;
      const monthlyValues = yv.item.monthlyValues;
      const oldLoans = this.oldLoans?.items[typeLoan];
      if (oldLoans === undefined || monthlyValues == undefined || this.oldLoans?.items.year != yv.year) return;
      monthlyValues.forEach((_, idx) => {
        if (disableMonths[idx]) {
          monthlyValues[idx] = oldLoans[idx];
        }
      });
    };
    this.transcribeToList(set短期借入金, 貸借TypeList, [E集計転記科目.有利子負債短期借入金]);
    this.transcribeToList(set長期借入金, 貸借TypeList, [E集計転記科目.有利子負債長期借入金]);
    this.transcribeToList(set役員借入金, 貸借TypeList, [E集計転記科目.役員借入金]);
  }

  /**
   * 各部門の損益に転記
   *
   * @returns
   */
  protected transcribeToPls(): void {
    const 損益TypeLists = this.calcTypeLists.filter((t) => t.type === PlbscfType.損益計算書 && t.部門pk !== undefined);
    const 償却 = this.償却!;
    const 償却予測額 = 償却.total.償却予測額!;
    const disableMonths = this.calcTypeLists[0].header?.filter((h) => h.month).map((h) => h.disabled!);

    損益TypeLists.forEach((t) => {
      // ・償却：部門別減価償却費を「減価償却費」に転記
      const found販管製造 = 償却予測額.部門別販管製造s.find((b) => b.部門Pk === t.部門pk);
      const set販管費 = (yv: PlbscfYearValue): void => {
        set販管製造費(yv, true);
      };
      const set製造費 = (yv: PlbscfYearValue): void => {
        set販管製造費(yv, false);
      };
      const set販管製造費 = (yv: PlbscfYearValue, is販管: boolean): void => {
        const foundValue = found販管製造?.販管製造s.find((v, i) => 償却.header[i].year === yv.year);
        const 販管Value = foundValue === undefined ? 0 : foundValue.販管;
        const 製造Value = foundValue === undefined ? 0 : foundValue.製造;
        let value = 0;
        if (AbstractデータService.事業所.c製造原価科目未使用フラグ === E製造原価科目未使用フラグ.使用しない) {
          // 製造原価科目未使用の場合、販管費に合計値を設定／製造費は0
          value = is販管 ? 販管Value + 製造Value : 0;
        } else {
          value = is販管 ? 販管Value : 製造Value;
        }
        const fixedMonthlyValues = yv.item.monthlyValues ? JSON.parse(JSON.stringify(yv.item.monthlyValues)) : [];
        this.setYearValue(yv, value, true);
        const monthlyValues = yv.item.monthlyValues;
        const yearDiff = 決算期Utility.diffYearFrom0期(yv.year);
        if (yearDiff > 0 || monthlyValues === undefined || fixedMonthlyValues === undefined) return;
        let yValue = 0;
        monthlyValues.forEach((_, idx) => {
          if (disableMonths[idx]) monthlyValues[idx] = fixedMonthlyValues[idx];
          yValue += monthlyValues[idx];
        });
        yv.item.value = yValue === undefined ? 0 : yValue;
        yv.item.yValue = yValue === undefined ? 0 : yValue;
      };

      const set支払利息 = (yv: PlbscfYearValue): void => {
        const yearDiff = 決算期Utility.diffYearFrom0期(yv.year);
        const monthlyValues = yv.item.monthlyValues;
        const oldValues = yv.item.oldMonthlyValues;
        if (yearDiff < 0 || monthlyValues == undefined || oldValues == undefined) return;
        let value = 0;
        // Setting for value and monthly values if has old data
        monthlyValues.forEach((val, idx) => {
          if (disableMonths[idx + yearDiff * 12]) {
            monthlyValues[idx] = oldValues[idx];
            value += oldValues[idx];
          } else {
            value += val;
          }
        });
        yv.item.value = value === undefined ? 0 : value;
        yv.item.yValue = value === undefined ? 0 : value;
      };

      this.transcribeToList(set販管費, t, [E集計転記科目.減価償却費販管費]);
      this.transcribeToList(set製造費, t, [E集計転記科目.減価償却費製造原価]);
      this.transcribeToList(set支払利息, t, [E集計転記科目.支払利息]);
    });
  }

  /**
   * 補助科目設定可能かどうか
   *
   * @param k
   * @returns
   */
  protected canSet補助科目(k: 勘定科目ListItemResponse): boolean {
    return !補助科目設定不可大分類s.includes(k.c大分類);
  }

  /**
   * tデータPlbscfsを生成する
   *
   * @param plbscfデータs
   * @returns
   */
  protected createTデータPlbscfs(plbscfデータs: データListItemResponse[]): TデータPlbscf[] {
    const tデータPlbscfs: TデータPlbscf[] = [];
    for (const h of this.onlyYear決算期.header.filter((h) => 決算期Utility.diffYearFrom0期(h.決算年) >= 0)) {
      for (const 部門 of this.部門s) {
        tデータPlbscfs.push(this.createTデータPlbscf(plbscfデータs, h, 部門));
      }
      tデータPlbscfs.push(this.createTデータPlbscf(plbscfデータs, h));
    }
    return tデータPlbscfs;
  }

  /**
   * tデータ科目別計画Configを生成する
   *
   * @param データs
   * @param e科目別計画設定番号
   * @returns
   */
  protected createTデータ科目別計画Configs(
    データs: データListItemResponse[],
    e科目別計画設定番号: E科目別計画設定番号,
    isYear = false
  ): Tデータ科目別計画Config[] {
    return this.入力分類Filter(データs, [isYear ? E入力分類.YEARCONFIG : E入力分類.科目別計画設定])
      .filter((t) => t.c番号 === e科目別計画設定番号)
      .map((d) => ({
        データpk: d.pk,
        部門pk: d.c部門pK!,
        items: JSON.parse(d.cデータ),
      }));
  }

  /**
   * 指定決算年に月次データがあるかどうか
   *
   * @param v決算年
   * @returns
   */
  protected isMonthlyBy決算年(v決算年: 決算年): boolean {
    return v決算年.is計画01;
  }

  /**
   * 科目別計画のモニタリング用の項目を作成する
   *
   * @param _list
   * @param _勘定科目Pk
   * @param _通常科目Pk
   * @param _科目別計画明細No
   * @returns
   */
  protected createMonitoringFor科目別計画(
    _list: 科目別計画ListItem[],
    _勘定科目Pk: number,
    _通常科目Pk: number,
    _科目別計画明細No?: number
  ): { yearItem: PlbscfYearItem; 業績予測: 業績予測 } | undefined {
    return;
  }

  /**
   * 三表のモニタリング用の項目を作成する
   *
   * @param _typeList
   * @param _i
   * @returns
   */
  protected createMonitoringForPlbscf(
    _typeList: PlbscfTypeList | undefined,
    _i: number
  ): { yearValue: PlbscfYearValue } | undefined {
    return;
  }

  /**
   * 貸借CalcConfigを作成する
   *
   * @returns
   */
  protected create貸借CalcConfig(): 貸借CalcConfig | undefined {
    return;
  }

  /**
   * 科目別計画データを絞り込み
   *
   * @param is0円非表示
   * @returns
   */
  protected filter科目別計画(is0円非表示: boolean): 科目別計画[] {
    const 製造原価科目Pks = this.勘定科目s
      .filter((勘定科目) => 勘定科目.cカテゴリ === E勘定科目カテゴリ.製造原価)
      .map((勘定科目) => 勘定科目.pk);
    return this.calc科目別計画s.map((科目別計画) => {
      let list = this.filter科目別List(科目別計画.list, is0円非表示);
      if (AbstractデータService.事業所.c製造原価科目未使用フラグ === E製造原価科目未使用フラグ.使用しない) {
        list = list.filter((listItem) => !製造原価科目Pks.includes(listItem.勘定科目Pk));
      }
      return {
        ...科目別計画,
        list,
      };
    });
  }

  /**
   * 科目別計画データを絞り込み
   *
   * @param list
   * @param is0円非表示
   * @returns
   */
  protected filter科目別List<T extends 科目別ListItem>(list: T[], is0円非表示: boolean): T[] {
    const filteredList = list.map((listItem) => ({
      ...listItem,
      yearItems: this.filterYearItems(listItem.yearItems),
    }));
    if (!is0円非表示) return filteredList;
    const notZeroList: T[] = [];
    for (let i = 0; i < filteredList.length; ) {
      if (filteredList[i].通常科目 === undefined) {
        i++;
        continue;
      }

      // 現在の通常科目に紐づく行データに、0でない値が存在すればnotZeroListに追加
      let itemCount = 0;
      let isNotZero = false;
      for (let j = i; j < filteredList.length; j++) {
        if (j > i && filteredList[j].通常科目 !== undefined) {
          itemCount = j - i;
          break;
        }
        if (!isNotZero) {
          isNotZero = filteredList[j].yearItems.find((yi) => this.isNotZeroYearItem(yi)) !== undefined;
          const monitoringYi = filteredList[j].monitoring?.yearItem;
          if (!isNotZero && monitoringYi !== undefined && this.isNotZeroYearItem(monitoringYi)) {
            isNotZero = true;
          }
        }
        if (j === filteredList.length - 1) itemCount = j - i + 1;
      }
      if (isNotZero) {
        for (let j = i; j < i + itemCount; j++) notZeroList.push(filteredList[j]);
      }
      i += itemCount;
    }
    return notZeroList;
  }

  /**
   * YearItemsを計画書データの状態に応じて絞り込み
   *
   * @param yearItems
   * @returns
   */
  protected filterYearItems(yearItems: PlbscfYearItem[]): PlbscfYearItem[] {
    return yearItems;
  }

  /**
   * 入力可能な月かどうか
   *
   * @param _monthIndex
   * @returns
   */
  protected isEditableMonth(_monthIndex: number): boolean {
    return true;
  }

  /**
   * 科目別の帳票データを生成
   *
   * @param v科目別損益計算書
   * @param sheetNamePrefix
   * @returns
   */
  protected create科目別帳票データs(
    v科目別損益計算書: {
      header: 決算期TableHeader[];
      科目別計画s: 科目別計画[];
    },
    sheetNamePrefix: string,
    isYear = false
  ): 帳票データ[] {
    // テンプレート行設定
    type 科目別CssClass = {
      blue: number;
    };
    const templateConfig = {
      header: 0,
      subheader: 1,
      row: {
        body: 2,
        total: 3,
        grey: 4,
        blue: 5,
      },
    };

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const staticColumnCount = 4;
    const initialTemplateRows: number[] = [templateConfig.header, templateConfig.subheader];
    const initialValues: unknown[][] = [
      [
        AbstractデータService.getMoneyUnitLabel(),
        ...this.templateCellValues(v科目別損益計算書.header.length + staticColumnCount - 1),
      ],
      [...this.templateCellValues(staticColumnCount), ...v科目別損益計算書.header.map((h) => h.label)],
    ];

    // 部門ごとに行でループ
    return v科目別損益計算書.科目別計画s.map((v科目別計画) => {
      const v部門 = this.部門s.find((b) => b.pk === v科目別計画.部門pk)!;
      const templateRows = _.cloneDeep(initialTemplateRows);
      const values = _.cloneDeep(initialValues);
      let 比率index = -1;
      for (const listItem of v科目別計画.list) {
        // 表示対象行のみ
        if (!Abstract計画PlbscfService.isVisible科目別計画ListItem(listItem, isYear)) continue;

        let config = templateConfig.row.body;
        if (listItem.option?.cssClass !== undefined) {
          config = templateConfig.row[listItem.option.cssClass as keyof 科目別CssClass];
        } else if ((isYear && listItem.yConfig === undefined) || (!isYear && listItem.config === undefined)) {
          const 補助科目 = isYear ? listItem.y補助科目 : listItem.補助科目;
          config =
            補助科目 !== undefined && 補助科目.name.length === 0 ? templateConfig.row.grey : templateConfig.row.total;
        }
        templateRows.push(config);

        // 一行追加
        const datas: (string | number | undefined)[] = !isYear
          ? [
              listItem.通常科目 === undefined ? '' : listItem.通常科目.name,
              listItem.補助科目 === undefined ? '' : listItem.補助科目.name,
              listItem.計算項目No === undefined ? '' : listItem.計算項目No,
              listItem.config === undefined ? listItem.option!.label : listItem.config.label,
            ]
          : [
              listItem.y通常科目 === undefined ? '' : listItem.y通常科目?.name,
              listItem.y補助科目 === undefined ? '' : listItem.y補助科目?.name,
              listItem.y計算項目No === undefined ? '' : listItem.y計算項目No,
              listItem.yConfig === undefined ? listItem.option!.label : listItem.yConfig?.label,
            ];
        listItem.yearItems.forEach((yi, i) => {
          const 計画0期Year = 決算期Utility.get計画0期の決算年(true);
          const iValue =
            isYear && yi.year > 計画0期Year
              ? yi.item.yValue
              : yi.item.科目別計画明細No === 科目別計画明細_CONFIG_COUNT
              ? yi.item.value
              : 0;
          datas.push(this.getMoneyUnitValue(iValue));

          // 業績予測が存在する場合は追加
          const 業績予測 = listItem.monitoring?.業績予測;
          if (i === listItem.yearItems.length - 1 && 業績予測 !== undefined) {
            datas.push(
              業績予測.差異 === undefined ? '' : this.getMoneyUnitValue(業績予測.差異),
              業績予測.比率 === undefined ? '' : 業績予測.比率 + '%'
            );
            比率index = datas.length - 1;
          }

          if (yi.withMonths && yi.item.monthlyValues !== undefined) {
            yi.item.monthlyValues.forEach((mv) => datas.push(this.getMoneyUnitValue(mv)));
          }
        });
        values.push(datas);
      }
      return {
        sheetName: this.createXlsxSheetName(`${sheetNamePrefix}-${v部門.c部門名}`),
        templateRows,
        values,
        dynamicConfigs: this.create帳票DynamicConfig(v科目別損益計算書.header, staticColumnCount, 比率index),
      };
    });
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * CデータPlbscfItem 配列中の指定勘定科目Pk、科目別計画明細Noに紐付く要素のインデックスを取得
   *
   * @param cデータPlbscfItems
   * @param 勘定科目Pk
   * @param 科目別計画明細No
   * @returns
   */
  private find勘定科目ItemIndex(
    cデータPlbscfItems: CデータPlbscfItem[],
    勘定科目Pk?: number,
    科目別計画明細No?: number
  ): number {
    const no = 科目別計画明細No === undefined ? 科目別計画明細_CONFIG_COUNT : 科目別計画明細No;
    return cデータPlbscfItems.findIndex(
      (item) => item.勘定科目Pk !== undefined && item.勘定科目Pk === 勘定科目Pk && item.科目別計画明細No === no
    );
  }

  /**
   * CデータPlbscfItemを生成
   * DBデータが存在すれば取得、存在しない場合は生成・DBデータに設定したうえで返却
   *
   * @param cデータPlbscfItems
   * @param 決算年
   * @param 勘定科目Pk
   * @param 科目別計画明細No
   * @returns
   */
  private findOrCreate勘定科目Item(
    cデータPlbscfItems: CデータPlbscfItem[],
    決算年: 決算年,
    勘定科目Pk?: number,
    科目別計画明細No?: number
  ): CデータPlbscfItem {
    let item: CデータPlbscfItem;
    const index = this.find勘定科目ItemIndex(cデータPlbscfItems, 勘定科目Pk, 科目別計画明細No);
    if (index === -1) {
      // DBに存在しないなら追加
      item = this.createEmpty勘定科目Item(決算年, 勘定科目Pk, 科目別計画明細No);
      cデータPlbscfItems.push(item);
    } else {
      item = cデータPlbscfItems[index];
    }
    return item;
  }

  /**
   * 空のCデータPlbscfItemを生成
   *
   * @param v決算年
   * @param 勘定科目Pk
   * @param 科目別計画明細No
   * @returns
   */
  private createEmpty勘定科目Item(v決算年: 決算年, 勘定科目Pk?: number, 科目別計画明細No?: number): CデータPlbscfItem {
    return {
      勘定科目Pk,
      科目別計画明細No: 科目別計画明細No === undefined ? 科目別計画明細_CONFIG_COUNT : 科目別計画明細No,
      value: 0,
      monthlyValues: this.isMonthlyBy決算年(v決算年) ? new Array(MONTHS_OF_YEAR).fill(0) : undefined,
    };
  }

  /**
   * 科目別計画明細Formulaの内容に応じた年ごとの計算関数
   *
   * @param formula
   * @param 同科目ListItems
   * @param yearItemsIndex
   * @param key
   * @param monthlyValuesIndex
   * @returns
   */
  private calculateByConfig = (
    formula: 科目別計画明細Formula,
    同科目ListItems: 科目別計画ListItem[],
    yearItemsIndex: number,
    key: 'value' | 'monthlyValues' | 'yValue',
    monthlyValuesIndex?: number
  ): number => {
    let calcListItems: 科目別計画ListItem[];
    if (key === 'yValue') {
      calcListItems = formula.nos.map((n) => 同科目ListItems.find((li) => n === li.yConfig?.科目別計画明細No)!);
    } else {
      calcListItems = formula.nos.map((n) => 同科目ListItems.find((li) => n === li.config?.科目別計画明細No)!);
    }
    // const calcListItems = formula.nos.map((n) => 同科目ListItems.find((li) => n === li.config?.科目別計画明細No)!);
    const values = calcListItems.map((cli) => {
      if (key === 'value') return cli.yearItems[yearItemsIndex].item.value!;
      if (key === 'yValue') return cli.yearItems[yearItemsIndex].item.yValue!;
      return cli.yearItems[yearItemsIndex].item.monthlyValues![monthlyValuesIndex!];
    });
    const scriptFormula = Abstract計画PlbscfService.createFormula(values, formula.operators, true);
    const evaled: number = eval(scriptFormula);

    // 0割りなどは 0 に変換
    const result = !isFinite(evaled) || isNaN(evaled) ? 0 : evaled;
    return this.roundFunc(result);
  };

  /**
   * 対象の勘定科目行に値を転記する
   *
   * @param setYearValue
   * @param typeList
   * @param columnValues
   * @returns
   */
  private transcribeToList(
    setYearValue: (yv: PlbscfYearValue) => void,
    typeList: PlbscfTypeList,
    columnValues: E集計転記科目[]
  ): void {
    typeList.list
      .filter((listItem) => {
        // 対象の勘定科目行のみを抽出
        if (listItem.勘定科目 === undefined) return false;
        for (const c of columnValues) {
          if (c === listItem.勘定科目.c集計転記科目) return true;
        }
        return false;
      })
      .forEach((listItem) => {
        listItem.yearValues.forEach((yv) => {
          // year：過去決算期の場合、何もしない
          if (決算期Utility.diffYearFrom0期(yv.year) < 0) return;
          if (!columnValues.includes(E集計転記科目.支払利息)) {
            yv.type = YearItemType.readonly;
          }
          setYearValue(yv);
        });
      });
  }

  /**
   * 分析データ一行から、計画データ一行を生成する（科目別用）
   *
   * @param v分析ListItem
   * @param v決算期
   * @param 部門pk
   * @param config
   * @returns
   */
  private create科目別YearItems(v分析ListItem: PlbscfListItem, v決算期: 決算期, 部門pk?: number): PlbscfYearItem[] {
    return v決算期.years.map((v決算年) => {
      const yv = this.createYearValue(v分析ListItem, v決算年, [], 部門pk, false, true);
      return {
        year: yv.year,
        item: yv.item,
        type: yv.type,
      };
    });
  }

  /**
   * 分析データから、PlbscfYearValueを生成する
   *
   * @param v分析ListItem
   * @param v決算年
   * @param calcYearValues
   * @param 部門pk
   * @param is貸借
   * @returns
   */
  private createYearValue(
    v分析ListItem: PlbscfListItem,
    v決算年: 決算年,
    calcYearValues: PlbscfYearValue[],
    部門pk?: number,
    is貸借 = false,
    is損益 = false
  ): PlbscfYearValue {
    const v分析Item = v分析ListItem.yearValues[0].item;
    if (v決算年.eフェーズ種別 === Eフェーズ種別.分析フェーズ) {
      // 分析列
      const found分析YearValue = v分析ListItem.yearValues.find((yv) => yv.year === v決算年.year)!;
      const yearItemType = found分析YearValue.type === undefined ? YearItemType.readonly : found分析YearValue.type;
      if (
        (is損益 && found分析YearValue.item.yValue === undefined && found分析YearValue.item.value !== undefined) ||
        (is貸借 && found分析YearValue.item.value !== undefined)
      ) {
        found分析YearValue.item.yValue = found分析YearValue.item.value;
      }
      return {
        ...found分析YearValue,
        type: yearItemType,
      };
    }

    // 計画列
    // 現在年のDBデータを取得
    const cデータPlbscfItems = this.findCデータPlbscfItems(v決算年, 部門pk);
    // value：undefined可として扱う
    const partialItems = cデータPlbscfItems as PartialCデータPlbscfItem[];
    const isMonthly = this.isMonthlyBy決算年(v決算年);

    // 行のconfig情報に応じてyearValueを設定
    let yearValue: PlbscfYearValue = {
      item: {},
      year: v決算年.year,
    };
    switch (v分析ListItem.config.valueType) {
      case ValueType.noValue:
        break;
      case ValueType.勘定科目:
        // 既に科目がcalcTypeListに存在するか確認
        const foundYv = this.filterYearValuesByYear(calcYearValues, v決算年.year).find(
          (yv) => yv.item.勘定科目Pk !== undefined && yv.item.勘定科目Pk === v分析Item.勘定科目Pk
        );
        if (foundYv === undefined) {
          yearValue.item = this.findOrCreate勘定科目Item(cデータPlbscfItems, v決算年, v分析Item.勘定科目Pk);
        } else {
          yearValue = foundYv;
        }
        // 貸借 かつ 月次データが存在する場合、最終月の値を表示させたいので設定
        if (is貸借 && isMonthly && yearValue.item.monthlyValues !== undefined) {
          yearValue.optionValue = yearValue.item.monthlyValues[MONTHS_OF_YEAR - 1];
        }
        break;
      default:
        let foundItem: PartialCデータPlbscfItem | undefined = partialItems.find(
          (item) => item.計算Code !== undefined && item.計算Code === v分析Item.計算Code
        );
        if (foundItem === undefined) {
          // DBに存在しないなら追加
          foundItem = {
            計算Code: v分析Item.計算Code,
          };
          partialItems.push(foundItem);
        }
        yearValue.item = foundItem;
        yearValue.calcValue = 0;
        yearValue.yCalcValue = 0;
        yearValue.monthlyCalcValues = isMonthly ? new Array(MONTHS_OF_YEAR).fill(0) : undefined;
        break;
    }
    if (
      (is損益 && yearValue.item.yValue === undefined && yearValue.item.value !== undefined) ||
      (is貸借 && yearValue.item.value !== undefined)
    ) {
      yearValue.item.yValue = yearValue.item.value;
    }
    // set value = yValue when 計画 > 1 (old data)
    if (is損益 && yearValue.item.monthlyValues === undefined && yearValue.item.yValue != undefined) {
      yearValue.item.value = yearValue.item.yValue;
    }
    return yearValue;
  }

  /**
   * DBデータから 指定CデータPlbscfItem を削除
   *
   * @param v決算年
   * @param 勘定科目Pk
   * @param 部門pk
   * @param config
   * @returns
   */
  private removeCデータPlbscfItems(
    v決算年: 決算年,
    勘定科目Pk?: number,
    部門pk?: number,
    config?: 科目別計画明細Config
  ): void {
    const cデータPlbscfItems = this.findCデータPlbscfItems(v決算年, 部門pk);
    const index = this.find勘定科目ItemIndex(cデータPlbscfItems, 勘定科目Pk, config?.科目別計画明細No);
    if (index > -1) cデータPlbscfItems.splice(index, 1);
  }

  /**
   * DBデータから、指定決算年・部門に紐づくCデータPlbscfItem一覧を取得する
   *
   * @param v決算年
   * @param 部門pk
   * @returns
   */
  private findCデータPlbscfItems(v決算年: 決算年, 部門pk?: number): CデータPlbscfItem[] {
    return this.rawTデータPlbscfs.find(
      (tデータPlbscf) => tデータPlbscf.番号 === v決算年.year && tデータPlbscf.部門pk === 部門pk
    )!.items;
  }

  /**
   * 指定年・部門のデータがDBに登録されているかどうか
   *
   * @param year
   * @param 部門pk
   * @returns
   */
  private hasデータSaved(year: number, 部門pk?: number): boolean {
    return (
      this.rawTデータPlbscfs.filter((t) => t.データpk !== undefined && t.番号 === year && t.部門pk === 部門pk).length >
      0
    );
  }

  /**
   * tデータPlbscfを生成する
   *
   * @param plbscfデータs
   * @param header
   * @param 部門
   * @returns
   */
  private createTデータPlbscf(
    plbscfデータs: データListItemResponse[],
    header: 決算期TableHeader,
    部門?: 部門ListItemResponse
  ): TデータPlbscf {
    const foundデータ = plbscfデータs.find((データ) => データ.c番号 === header.year && データ.c部門pK === 部門?.pk);
    let parseItems = [] as CデータPlbscfItem[];
    if (foundデータ !== undefined) {
      parseItems = JSON.parse(foundデータ.cデータ) as CデータPlbscfItem[];
      if (parseItems[0].yValue === undefined) {
        parseItems.forEach((item) => {
          item.yValue = item.value;
        });
      }
    }
    return {
      データpk: foundデータ?.pk,
      番号: header.year,
      部門pk: 部門?.pk,
      items: parseItems,
    };
  }

  /**
   * PlbscfYearValue に値を設定
   *
   * @param yv
   * @param value
   * @returns
   */
  private setYearValue(yv: PlbscfYearValue, value?: number, isPls = false): void {
    yv.item.value = value === undefined ? 0 : value;
    if (isPls) yv.item.yValue = value === undefined ? 0 : value;
    if (yv.item.monthlyValues === undefined) return;
    AbstractデータService.divideYearlyValue(yv.item, yv.item.value);
  }
}
